import { useState } from 'react';
import { Search, X, User, Briefcase, DollarSign, CreditCard } from 'lucide-react';
import { adminService } from '../lib/adminService';
import { useAuthStore } from '../store/authStore';

interface QuickActionsProps {
  onClose: () => void;
}

export function AdminQuickActions({ onClose }: QuickActionsProps) {
  const { profile } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const { data } = await adminService.getAllUsers({
        search: searchQuery,
        limit: 10,
      });
      setSearchResults(data || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (user: any) => {
    setLoading(true);
    try {
      const details = await adminService.getUserWithDetails(user.id);
      setSelectedUser({ ...user, ...details });
      setSearchResults([]);
      setSearchQuery('');
    } catch (error) {
      console.error('Error loading user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustCredits = async () => {
    if (!selectedUser || !creditAmount || !creditReason || !profile) return;

    setLoading(true);
    setMessage(null);
    try {
      await adminService.adjustUserCredits(
        profile.id,
        selectedUser.id,
        parseInt(creditAmount),
        creditReason
      );
      setMessage({ type: 'success', text: 'Credits adjusted successfully!' });
      setCreditAmount('');
      setCreditReason('');

      const details = await adminService.getUserWithDetails(selectedUser.id);
      setSelectedUser({ ...selectedUser, ...details });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to adjust credits' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Quick Actions</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search Users
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
              >
                Search
              </button>
            </div>
          </div>

          {searchResults.length > 0 && (
            <div className="mb-6 space-y-2">
              <p className="text-sm font-semibold text-gray-700">Search Results:</p>
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className="w-full p-4 bg-gray-50 hover:bg-gray-100 rounded-lg text-left transition border-2 border-transparent hover:border-blue-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{user.full_name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'job_seeker'
                          ? 'bg-green-100 text-green-800'
                          : user.role === 'employer'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}
                    >
                      {user.role}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedUser && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{selectedUser.full_name}</h3>
                    <p className="text-gray-600">{selectedUser.email}</p>
                  </div>
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-semibold ${
                      selectedUser.role === 'job_seeker'
                        ? 'bg-green-500 text-white'
                        : selectedUser.role === 'employer'
                        ? 'bg-blue-500 text-white'
                        : 'bg-orange-500 text-white'
                    }`}
                  >
                    {selectedUser.role}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="w-4 h-4 text-orange-600" />
                      <p className="text-xs text-gray-600">Credits</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {selectedUser.credits?.balance || 0}
                    </p>
                  </div>

                  {selectedUser.role === 'job_seeker' && (
                    <div className="bg-white rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Briefcase className="w-4 h-4 text-blue-600" />
                        <p className="text-xs text-gray-600">Applications</p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedUser.applications?.length || 0}
                      </p>
                    </div>
                  )}

                  {selectedUser.role === 'employer' && (
                    <div className="bg-white rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Briefcase className="w-4 h-4 text-blue-600" />
                        <p className="text-xs text-gray-600">Jobs Posted</p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedUser.jobs?.length || 0}
                      </p>
                    </div>
                  )}

                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <p className="text-xs text-gray-600">Subscription</p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      {selectedUser.subscriptions?.[0]?.plan_type || 'Free'}
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-gray-600" />
                      <p className="text-xs text-gray-600">Joined</p>
                    </div>
                    <p className="text-xs font-semibold text-gray-900">
                      {new Date(selectedUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {message && (
                <div
                  className={`p-4 rounded-lg ${
                    message.type === 'success'
                      ? 'bg-green-100 text-green-900 border-2 border-green-300'
                      : 'bg-red-100 text-red-900 border-2 border-red-300'
                  }`}
                >
                  {message.text}
                </div>
              )}

              <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-orange-600" />
                  Adjust Credits
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Amount (positive to add, negative to remove)
                    </label>
                    <input
                      type="number"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      placeholder="e.g., 100 or -50"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Reason for Adjustment
                    </label>
                    <textarea
                      value={creditReason}
                      onChange={(e) => setCreditReason(e.target.value)}
                      placeholder="e.g., Customer support credit adjustment"
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <button
                    onClick={handleAdjustCredits}
                    disabled={loading || !creditAmount || !creditReason}
                    className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Adjusting...' : 'Adjust Credits'}
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <a
                  href={`/admin/users/${selectedUser.id}`}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition text-center justify-center"
                >
                  <User className="w-5 h-5" />
                  View Full Profile
                </a>
                <button
                  className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition justify-center"
                  onClick={() => setSelectedUser(null)}
                >
                  <X className="w-5 h-5" />
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          {loading && !selectedUser && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
